import type { Raw } from "vue"

import { uuidv7 } from "uuidv7"

import type { AgentStatusTurnPart } from "~/models/polyschema/AgentStatusTurnPart"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { ReactiveConversation } from "~/classes/conversation/ReactiveConversation"
import { StaticConversation } from "~/classes/conversation/StaticConversation"
import { DatagramErrorType, PropertySchema } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendConversationCreateRequests } from "~/classes/handlers/conversation/create"
import { sendConversationDeleteRequests } from "~/classes/handlers/conversation/delete"
import { sendSelfConversationsRequest } from "~/classes/handlers/user/conversations"

// TODO
// - [ ] Handle the `onAfterNavigatedToConversation` event

interface State {
  /**
   * Due to interior mutability, we don't want to update these objects when we receive
   * property updates -- instead we just want to ensure that we have the correct objects
   * in the map, but from our perspective they can be out of date.
   */
  all: Record<string, Raw<StaticConversation>>
  /**
   * current / latest conversation id, if any. Will get set from the
   * user's settings and updating this should trigger updates to the
   * settings.
   */
  current?: string
  /**
   * Whether we have loaded the user's conversations yet.
   */
  initialized?: boolean

  subscriptions: Record<string, boolean>
}

class Conversations extends GlobalComposable<State> {
  override defaultState = () => ({
    all: {},
    subscriptions: {},
  })

  override onCreate = () => {
    const { userInterfaceSettings } = useSelf()
    const { addGenericEntityEventListener, addInterfaceEventListener } = useEvents()
    addGenericEntityEventListener("conversationProperties", this._onConversationProperties)
    addInterfaceEventListener("selfConversationsResponse", this.onSelfConversationsResponse)
    addInterfaceEventListener("conversationPropertiesResponse", this._onConversationPropertyResponse)

    // Watch this once so that once the user's settings have been retrieved we
    // switch to that conversation, otherwise this would make us switch conversations
    // every time the user's settings change either here or on any tab.
    watchOnce(userInterfaceSettings, (settings) => {
      this.state.value.current = settings.latestConversationId
    })

    // TODO: watch when the current conversation changes and update properties
  }

  override onDestroy = () => {
    const { removeGenericEntityEventListener, removeInterfaceEventListener } = useEvents()
    removeGenericEntityEventListener("conversationProperties", this._onConversationProperties)
    removeInterfaceEventListener("selfConversationsResponse", this.onSelfConversationsResponse)
    removeInterfaceEventListener("conversationPropertiesResponse", this._onConversationPropertyResponse)
  }

  /**
   * Migrate to some useConversation / useAgent composable
   */
  requestSelfConversations = () => {
    const request: SelfConversationsRequestDatagram = {
      requestId: generateWorkerRequestId(),
      offline: false,
      selection: InterfaceSelection.minimumSelfConversationSelection().inner(),
    }
    sendSelfConversationsRequest(request)
    return request
  }

  onSelfConversationsResponse = async (response: SelfConversationsResponseDatagram) => {
    const { conversations, error, offline } = response

    if (error && !offline) {
      // for non-offline errors, then we need to log out
      this.error("Fatal error retrieving conversations, user has no agent :(", error)
      return
    }

    if (error || conversations === undefined)
      return this.info("Cached self conversations errored (likely first time logging in)")

    // add all conversations to the state and remove all that are not in the response
    const conversationIds = new Set(conversations.map(c => c.id))
    for (const conversation of conversations)
      this.addConversationToStateIfNew(StaticConversation.fromEvent(conversation))
    for (const id of Object.keys(this.state.value.all))
      if (!conversationIds.has(id))
        this.removeConversation(id)
    this.state.value.initialized = true
  }

  waitForConversationsToInitialize = async () => {
    if (this.state.value.initialized)
      return

    const { resolve, promise } = Promise.withResolvers<void>()
    const unwatch = watch(() => this.state.value.initialized, (state) => {
      if (state)
        resolve()
    })
    await promise
    unwatch()
    return
  }

  staticConversation = (conversationId?: string) => conversationId ? this.state.value.all[conversationId] : undefined

  getReactiveConversation = (conversationId: string, location: SelectionLocation, immediate: boolean): ReactiveConversation | undefined => {
    if (!isDefined(this.staticConversation(conversationId)))
      return
    const conversation = computed({
      get: () => this.staticConversation(conversationId)!,
      set: (conversation: StaticConversation) => {
        this.state.value.all[conversationId] = markRaw(conversation)
      },
    })
    return new ReactiveConversation({ conversation, location, options: { immediate } })
  }

  /**
   * Holds a reference to the current conversation, if any. Note that this is a computed object,
   * since you can't edit the current conversation directly. Instead, grab a reactive reference
   * to it, and update that reference.
   */
  currentConversation = computed(() => this.staticConversation(this.state.value.current))

  /**
   * We received conversation properties. This could mean:
   * 1. We or someone else changed the name
   * 2. The members changed, including us being removed and unsubscribed
   * 3. A message was sent in the conversation by us or someone else
   * 4. The conversation was closed / paused by us or someone else
   * 5. We fetched properties
   */
  _onConversationProperties = (conversation: ConversationEvent) => {
    const { state: myself } = useSelf()

    const iConversation = StaticConversation.fromEvent(conversation)

    // if we have been removed, remove the conversation and switch to the latest one
    if (myself.value.user && iConversation.isUserRemoved(myself.value.user.id)) {
      this.removeConversation(iConversation.id)
    }
    else
      this.addConversationToStateIfNew(iConversation)
  }

  /**
   * This handler is only to respond to errors in property requests. We may receive
   * them in a few instances:
   * - We try to navigate to a deleted conversation
   * - We refresh the page on a deleted conversation or open in our sidebar
   * - Someone else removes us from the conversation
   */
  _onConversationPropertyResponse = (response: ConversationPropertiesResponseDatagram) => {
    const { conversationId, error, offline } = response
    if (error && !offline) {
      this.error("Error retrieving conversation properties", error)
      // if we received an error, we should remove the conversation and switch to
      // another one, as this conversation might be deleted
      this.removeConversation(conversationId)
    }
  }

  /**
   * Transcript where subsequent agent fragments and status parts are merged in since
   * they are initially streamed. Iterates over the transcript, placing agent turns in
   * an array then pushing them as fragments before a user turn is pushed
   */
  mergedTranscript = (conversation: StaticConversation): ConversationTurn[] => {
    const properties = conversation.turnProperties()
    const turnWise: ConversationTurn[] = []
    let fragments: AgentOutputTurnFragment[] = []
    let parts: AgentStatusTurnPart[] = []

    const flushFragmentsIfNeeded = () => {
      if (len(fragments) === 0)
        return
      turnWise.push({ agent: { output: { fragments } } })
      fragments = []
    }
    const flushPartsIfNeeded = () => {
      if (len(parts) === 0)
        return
      turnWise.push({ agent: { status: { parts } } })
      parts = []
    }

    for (const property of properties) {
      if (property.value.type !== PropertySchema.ConversationTurnValue)
        throw new Error("Must be a turn")
      if ("agent" in property.value.turn && "output" in property.value.turn.agent) {
        flushPartsIfNeeded()
        fragments.push(...property.value.turn.agent.output.fragments)
      }
      else if ("agent" in property.value.turn && "status" in property.value.turn.agent) {
        flushFragmentsIfNeeded()

        // status messages are a bit of a hairball -- if we receive a "response" type message
        // we need to search backward in the turn-wise array to find the request and place the
        // response directly after it.

        for (const part of property.value.turn.agent.status.parts) {
          const requestId = this.agentStatusTurnPartRequestId(part)
          if (!requestId)
            continue

          // if we are not a response, we can just add ourselves to the list
          if (!this.agentStatusTurnIsResponse(part)) {
            parts.push(part)
            continue
          }

          // if the request is in our current parts, add ourselves after it
          const idx = parts.findIndex(p =>
            this.agentStatusTurnIsRequest(p)
            && this.agentStatusTurnPartRequestId(p) === requestId,
          )
          if (idx > -1) {
            parts.splice(idx + 1, 0, part)
            continue
          }

          // otherwise we need to search backwards in the turn-wise array to find the request
          // and add ourselves after it
          let foundRequest = false
          for (const turn of turnWise) {
            if (!("agent" in turn) || !("status" in turn.agent))
              continue
            const idx = turn.agent.status.parts.findIndex(p =>
              this.agentStatusTurnIsRequest(p)
              && this.agentStatusTurnPartRequestId(p) === requestId,
            )
            if (idx > -1) {
              turn.agent.status.parts.splice(idx + 1, 0, part)
              foundRequest = true
              break
            }
          }

          // if we still haven't found the request, just add ourselves to the end
          if (!foundRequest)
            parts.push(part)
        }
      }
      else {
        flushPartsIfNeeded()
        flushFragmentsIfNeeded()
        turnWise.push(property.value.turn)
      }
    }
    flushPartsIfNeeded()
    flushFragmentsIfNeeded()

    return turnWise
  }

  agentStatusTurnPartRequestId = (part: AgentStatusTurnPart): string | undefined => {
    if ("fileCreateRequested" in part)
      return part.fileCreateRequested.request.requestId
    else if ("fileCreateResponded" in part)
      return part.fileCreateResponded.response.requestId
    else if ("fileEditRequested" in part)
      return part.fileEditRequested.request.requestId
    else if ("fileEditResponded" in part)
      return part.fileEditResponded.response.requestId
    else if ("fileLoadRequested" in part)
      return part.fileLoadRequested.request.requestId
    else if ("fileLoadResponded" in part)
      return part.fileLoadResponded.response.requestId
    else if ("fileMoveRequested" in part)
      return part.fileMoveRequested.request.requestId
    else if ("fileMoveResponded" in part)
      return part.fileMoveResponded.response.requestId
    else if ("fileUpdateRequested" in part)
      return part.fileUpdateRequested.request.requestId
    else if ("fileUpdateResponded" in part)
      return part.fileUpdateResponded.response.requestId
    else if ("searchListRequested" in part)
      return part.searchListRequested.request.requestId
    else if ("searchListResponded" in part)
      return part.searchListResponded.response.requestId
    else
      return undefined
  }

  agentStatusTurnIsRequest = (part: AgentStatusTurnPart): boolean => {
    return "fileCreateRequested" in part
      || "fileEditRequested" in part
      || "fileLoadRequested" in part
      || "fileMoveRequested" in part
      || "fileUpdateRequested" in part
      || "searchListRequested" in part
  }

  agentStatusTurnIsResponse = (part: AgentStatusTurnPart): boolean => {
    return "fileCreateResponded" in part
      || "fileEditResponded" in part
      || "fileLoadResponded" in part
      || "fileMoveResponded" in part
      || "fileUpdateResponded" in part
      || "searchListResponded" in part
  }

  addConversationToStateIfNew = (conversation: StaticConversation) => {
    if (isDefined(this.staticConversation(conversation.id)))
      return
    this.state.value.all[conversation.id] = markRaw(conversation)
  }

  removeConversation = (id: string) => {
    delete this.state.value.all[id]
    delete this.state.value.subscriptions[id]
    if (this.state.value.current === id)
      delete this.state.value.current
  }

  /**
   * Conversations ordered by their updated date
   */
  conversationsByUpdatedAt = computed(() => {
    return Object.values(this.state.value.all)
      .sort((a, b) => b.lastUpdated().getTime() - a.lastUpdated().getTime())
  })

  latestConversation = computed(() => first(this.conversationsByUpdatedAt.value))

  createNewConversation = async (timeoutMs = 5000) => {
    const { waitForFirstDataResponse } = useEvents()
    const { notifyDatagramError } = useAlerts()
    const { isAuthenticated } = useAuthentication()
    const { state: myself } = useSelf()
    if (!isAuthenticated() || !myself.value.user)
      throw new Error("Cannot create a conversation without being signed in")
    const userId = myself.value.user.id

    const preHash = StaticConversation.clientPreHash()
    const timestamp = uuidv7()
    const conversationId = await StaticConversation.idFromPrehash(preHash)
    let conversation = StaticConversation
      .blank(conversationId)
      .withSelfOwner(userId, timestamp)
      .withCreatedAt(userId, timestamp)

    const requestId = generateWorkerRequestId()
    sendConversationCreateRequests([{
      requestId,
      offline: false,
      conversation: conversation.inner(),
      preHash,
    }])

    const response = await withTimeout(
      waitForFirstDataResponse("conversationCreateResponse", requestId),
      timeoutMs,
    )

    if (response.error || !response.conversation) {
      notifyDatagramError(response.error!, "Failed to create conversation")
      throw new Error("Failed to create conversation")
    }

    conversation = new StaticConversation(response.conversation)

    this.addConversationToStateIfNew(conversation)

    return conversation
  }

  /**
   * When selecting a conversation from the chats menu or opening the panel
   * to the default, call this method to set the conversation state to the
   * correct values.
   */
  setCurrentConversation = (conversation: StaticConversation) => {
    if (conversation && conversation.id === this.state.value.current) {
      console.info("Already on the conversation", conversation.id)
      return conversation
    }

    this.state.value.current = conversation.id

    if (!this.state.value.subscriptions[conversation.id])
      this.subscribeToConversation(conversation)
  }

  /**
   * If the latest conversation is empty, we go to it to save us from creating a new one.
   * If it isn't empty, then we must create a new one, so that we can give the user an
   * empty conversation to start with.
   * @deprecated use the `onCreateOrOpenLatestConversationIntent` instead
   */
  getLatestIfEmptyOrCreateConversation = async () => {
    if (this.latestConversation.value?.isEmpty())
      return this.latestConversation.value

    const newConversation = await this.createNewConversation()
    return newConversation
  }

  /**
   * If there is no latest conversation we have to create a new one.
   */
  deleteConversation = (id: string) => {
    const { state: myself } = useSelf()
    if (!myself.value.user)
      throw new Error("Cannot delete a conversation without being signed in")

    const conversation = StaticConversation.blank(id)
    conversation.addIsDeleted(myself.value.user.id, uuidv7())
    sendConversationDeleteRequests([{
      requestId: generateWorkerRequestId(),
      offline: false,
      conversation: conversation.inner(),
    }])
    this.removeConversation(id)
  }

  matchingConversations = async (query: string): Promise<StaticConversation[]> => {
    const { cacheWorker } = useCache()

    let conversationIds: string[] = []
    if (isDesktop()) {
      const { findMatchingConversations } = useTauri()
      conversationIds = await findMatchingConversations(query)
    }
    else {
      conversationIds = await cacheWorker().findMatchingConversations(query)
    }

    return conversationIds
      .map(id => this.state.value.all[id])
      .filter(c => isDefined(c))
  }

  subscribeToConversation = async (conversation: StaticConversation) => {
    if (this.state.value.subscriptions[conversation.id])
      return console.debug("Already subscribed to conversation", conversation.id)

    const artifactFolderId = conversation.artifactsDirectoryId()
    if (!isDefined(artifactFolderId))
      return console.error("Cannot subscribe to conversation without artifacts directory id")
    const artifactsFolder = StaticFile.blank(artifactFolderId)

    const [conversationResult, folderResult] = await Promise.all([conversation.subscribe(), artifactsFolder.subscribe()])

    let errored = false
    if (isDefined(conversationResult.error)) {
      if (enumEq(conversationResult.error.type, DatagramErrorType.IgnoredDuplicateAction))
        console.warn("Already subscribed with that subscription id", conversation.id)
      else
        errored = true
    }

    if (isDefined(folderResult.error)) {
      if (enumEq(folderResult.error.type, DatagramErrorType.IgnoredDuplicateAction))
        console.warn("Already subscribed with that artifacts id", conversation.id)
      else
        errored = true
    }

    if (errored)
      return console.error("Error subscribing to conversation", conversation.id, conversationResult.error, folderResult.error)
    else
      this.state.value.subscriptions[conversation.id] = true
  }
}

const conversations = new Conversations("conversations", theme.colors.amber.hex)
export default function () {
  return conversations.use()
}
